import { ExternalProduct, Product } from '../models/product/types';
import {
    getMepTradeBuckets,
    normalizeProductMasterFormat,
    resolveMepMasterFormatSection,
} from '../pages/projectInfo/tabs/ReportingTab/tabs/LeedMEP/leedMepTopicRules';

type TcMfSectionPayload =
    | Array<ExternalProduct | Record<string, ExternalProduct>>
    | Record<string, ExternalProduct | ExternalProduct[]>
    | undefined
    | null;

function isExternalProduct(value: unknown): value is ExternalProduct {
    return (
        typeof value === 'object' &&
        value !== null &&
        'id' in value &&
        'productName' in value
    );
}

/** Subgroup-first MF fields from a TC external row (before MEP resolution). */
export function getExternalInitialMfFields(ext: ExternalProduct): { number: string; label: string } {
    return {
        number:
            ext.mfSectionLevel2?.number ||
            ext.mfSectionLevel3?.number ||
            ext.mfSectionLevel1?.number ||
            ext.mfSection?.number ||
            ext.lowestLevelNumber ||
            '',
        label:
            ext.mfSectionLevel2?.label ||
            ext.mfSectionLevel3?.label ||
            ext.mfSectionLevel1?.label ||
            ext.mfSection?.label ||
            ext.lowestLevelLabel ||
            '',
    };
}

function stampTcFiledMfSection(product: ExternalProduct, sectionKey: string): ExternalProduct {
    const filed = sectionKey.trim();
    if (!filed) {
        return product;
    }

    let filedLabel = '';

    if (product.mfSectionLevel2?.number === filed) {
        filedLabel = product.mfSectionLevel2.label;
    } else if (product.mfSection?.number === filed) {
        filedLabel = product.mfSection.label;
    } else {
        filedLabel =
            product.mfSectionLevel2?.label ??
            product.mfSection?.label ??
            '';
    }

    return {
        ...product,
        mfSection:
            product.mfSection?.number === filed
                ? product.mfSection
                : { number: filed, label: filedLabel },
        mfSectionLevel2:
            product.mfSectionLevel2?.number === filed
                ? product.mfSectionLevel2
                : { number: filed, label: filedLabel },
    };
}

/** Flatten TC `mfSection` buckets into one deduped list (Products + Reporting tabs). */
export function flattenTcMfSectionProducts(mfSection: TcMfSectionPayload): ExternalProduct[] {
    const flat: ExternalProduct[] = [];

    type ExternalProductEntry =
        | ExternalProduct
        | Record<string, ExternalProduct>
        | null
        | undefined;

    const pushEntry = (entry: ExternalProductEntry) => {
        if (!entry) {
            return;
        }
        if (isExternalProduct(entry)) {
            flat.push(entry);
            return;
        }
        for (const [sectionKey, product] of Object.entries(entry)) {
            if (!isExternalProduct(product)) {
                continue;
            }
            flat.push(stampTcFiledMfSection(product, sectionKey));
        }
    };

    if (Array.isArray(mfSection)) {
        mfSection.forEach(pushEntry);
    } else if (mfSection) {
        for (const [sectionKey, entry] of Object.entries(mfSection)) {
            if (Array.isArray(entry)) {
                entry.forEach((product) => {
                    if (isExternalProduct(product)) {
                        flat.push(stampTcFiledMfSection(product, sectionKey));
                    }
                });
            } else {
                pushEntry(entry);
            }
        }
    }

    return dedupeExternalCatalogProducts(flat);
}

/**
 * TC may return the same catalog product in multiple mfSection buckets.
 * Prefer the copy that maps to fewer MEP trade tables.
 */
export function dedupeExternalCatalogProducts(products: ExternalProduct[]): ExternalProduct[] {
    const byId = new Map<number, ExternalProduct[]>();
    for (const product of products) {
        const bucket = byId.get(product.id);
        if (bucket) {
            bucket.push(product);
        }
        else {
            byId.set(product.id, [product]);
        }
    }

    const result: ExternalProduct[] = [];
    for (const duplicates of byId.values()) {
        result.push(
            duplicates.length === 1 ? duplicates[0] : pickPreferredExternalDuplicate(duplicates),
        );
    }
    return result;
}

function externalAsProduct(ext: ExternalProduct): Product {
    const initial = getExternalInitialMfFields(ext);
    return normalizeProductMasterFormat({
        ...ext,
        lowestLevelNumber: initial.number,
        lowestLevelLabel: initial.label,
    } as Product);
}

function pickPreferredExternalDuplicate(duplicates: ExternalProduct[]): ExternalProduct {
    return duplicates.reduce((best, candidate) => {
        const bestCount = getMepTradeBuckets(externalAsProduct(best)).length;
        const candidateCount = getMepTradeBuckets(externalAsProduct(candidate)).length;
        if (candidateCount !== bestCount) {
            return candidateCount < bestCount ? candidate : best;
        }

        const bestMf = resolveMepMasterFormatSection(externalAsProduct(best)) ?? '';
        const candidateMf = resolveMepMasterFormatSection(externalAsProduct(candidate)) ?? '';
        return candidateMf.localeCompare(bestMf, undefined, { numeric: true }) < 0
            ? candidate
            : best;
    });
}
